import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  HStack,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  useFetchAccountsQuery,
  useFetchWorkOrderDetailQuery,
  useFetchWorkOrderTransactionsQuery,
} from 'api';
import { WorkOrderTransactionSummaryResponse } from 'api/bindings/WorkOrderTransactionSummaryResponse';
import { useAuth } from 'core';
import Decimal from 'decimal.js';
import {
  AfeLink,
  ApprovalsArrayIcon,
  ApproversSection,
  CommitmentVsBudgetBar,
  CurrencyLabel,
  DateDisplay,
  DateStack,
  InvoicedVsCommitmentBar,
  LoadingSkeleton,
  MultiBar,
  PageBody,
  PageCard,
  PageHeader,
  RevisionApprovalTable,
  UserLink,
  WorkOrderFormWorkFlowDrawerProps,
  WorkOrderWorkFlowDrawer,
  WorkOrderWorkFlowStage,
} from 'features';
import {
  DecisionDrawer,
  DecisionDrawerProps,
} from 'features/components/DecisionDrawer';
import { useMemo, useState } from 'react';
import { FiEdit, FiPlus } from 'react-icons/fi';
import { useParams } from 'react-router-dom';

export interface WorkOrderDetailPageProps {}

export interface WorkOrderDetailPageLocationProps {
  id: string;
}
export const WorkOrderDetailPage = (props: WorkOrderDetailPageProps) => {
  const { id: sId } = useParams();
  const id = sId || '0';
  const { data: wo, refetch } = useFetchWorkOrderDetailQuery(id, {
    skip: !id,
  });

  // Fetches
  const auth = useAuth();
  const { data: accountsResponse } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? "0" },
    { skip: !auth?.user.clientId }
  );
  const { data: transactions } = useFetchWorkOrderTransactionsQuery(
    { entityId: id, all: true },
    { skip: !id }
  );

  // State
  const [decisionDrawer, setDecisionDrawer] =
    useState<Omit<DecisionDrawerProps, 'onClose'>>();
  const [drawerSettings, setDrawerSettings] =
    useState<Omit<WorkOrderFormWorkFlowDrawerProps, 'onSubmit' | 'onClose'>>();
  const [toggledRevisionRow, setToggledRevisionRow] =
    useState<WorkOrderTransactionSummaryResponse>();
  const allSubAccounts = useMemo(
    () => accountsResponse?.items.flatMap((x) => x.subAccounts),
    [accountsResponse?.items]
  );

  // const pendingRevision = useOnlyPendingRevision(wo?.transactions);
  const pendingRevision = useMemo(
    () => transactions?.items.find((x) => !x.redeemed && !x.cancelled),
    [transactions?.items]
  );

  return (
    <Box>
      {drawerSettings && (
        <WorkOrderWorkFlowDrawer
          {...drawerSettings}
          onClose={() => setDrawerSettings(undefined)}
        />
      )}
      {decisionDrawer && (
        <DecisionDrawer
          {...decisionDrawer}
          // onDecision={refetch}
          onClose={() => setDecisionDrawer(undefined)}
        />
      )}
      {/* {isRevisionModalOpen && (
        <WorkOrderFormRevisionDrawerContent
          workOrderId={id}
          isOpen={true}
          onClose={() => setIsRevisionModalOpen(false)}
          onSubmit={onTxnSubmit}
        />
      )} */}
      <LoadingSkeleton isLoaded={!!wo}>
        <Stack spacing="8">
          {/* Header Two */}
          <PageHeader
            title={`Work Order: ${wo?.name}`}
            subTitle={wo?.description}
            topRightSection={
              <Button
                colorScheme="blue"
                variant="solid"
                leftIcon={
                  // @ts-ignore
                  <FiEdit />
                }
                onClick={() =>
                  setDrawerSettings({
                    ...drawerSettings,
                    initialStage: WorkOrderWorkFlowStage.workOrder,
                    workOrder: wo,
                    isOpen: true,
                  })
                }
              >
                Edit Work Order
              </Button>
            }
          >
            <Stack spacing={'4'}>
              <SimpleGrid columns={{ base: 1, md: 2 }}>
                <HStack>
                  <Text as="b">AFE:</Text>
                  <AfeLink afe={wo?.afe} />
                </HStack>
                <HStack>
                  <Text as="b">Asignee:</Text>
                  <UserLink u={wo?.assigneeUser} />
                </HStack>
                <HStack>
                  <Text as="b">Reference:</Text>
                  <Text>{wo?.reference}</Text>
                </HStack>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }}>
                <Text as="b">Budget:</Text>
                <MultiBar
                  max={[wo?.totalBudget, wo?.totalContingency]}
                  values={[wo?.total]}
                />
                <Text as="b">Invoiced:</Text>
                <MultiBar
                  max={[wo?.total]}
                  values={[wo?.invoiced, wo?.pendingInvoiced]}
                />
              </SimpleGrid>
            </Stack>
          </PageHeader>

          <Box as="section">
            {/* <Container> */}
            <SimpleGrid columns={{ base: 1, md: 5 }} gap={{ base: 5, md: 6 }}>
              <PageCard>
                <Stat>
                  <StatLabel noOfLines={1}>Total</StatLabel>
                  <StatNumber>
                    <CurrencyLabel value={wo?.total} />
                  </StatNumber>
                </Stat>
              </PageCard>
              <PageCard>
                <Stat>
                  <StatLabel noOfLines={1}>Pending</StatLabel>
                  <StatNumber>
                    <CurrencyLabel value={wo?.pending} />
                  </StatNumber>
                </Stat>
              </PageCard>
              <PageCard>
                <Stat>
                  <StatLabel noOfLines={1}>Invoiced</StatLabel>
                  <StatNumber>
                    <CurrencyLabel value={wo?.invoiced} />
                  </StatNumber>
                </Stat>
              </PageCard>
              <PageCard>
                <Stat>
                  <StatLabel noOfLines={1}>Budgeted</StatLabel>
                  <StatNumber>
                    <CurrencyLabel
                      value={new Decimal(wo?.totalBudget || '0')}
                    />
                  </StatNumber>
                </Stat>
              </PageCard>
              <PageCard>
                <Stat>
                  <StatLabel noOfLines={1}>Contingency</StatLabel>
                  <StatNumber>
                    <CurrencyLabel
                      value={new Decimal(wo?.totalContingency || '0')}
                    />
                  </StatNumber>
                </Stat>
              </PageCard>
            </SimpleGrid>
            {/* </Container> */}
          </Box>

          <Box as="section">
            <Flex gap={4}>
              <PageBody flexGrow={1}>
                <Tabs>
                  <TabList>
                    <Tab>Accounts</Tab>
                    {pendingRevision && <Tab>Approvers</Tab>}
                    <Tab>Revisions</Tab>
                    <Tab>Dates</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <Flex py={4}>
                        <Button
                          colorScheme="blue"
                          onClick={() =>
                            setDrawerSettings({
                              ...drawerSettings,
                              initialStage: WorkOrderWorkFlowStage.revision,
                              workOrder: wo,
                              isOpen: true,
                            })
                          }
                          leftIcon={
                            // @ts-ignore
                            <FiPlus />
                          }
                        >
                          Revision
                        </Button>
                      </Flex>
                      <Stack direction="column">
                        <TableContainer>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Account</Th>
                                <Th>Pending</Th>
                                <Th>Total</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {!wo?.accounts.length && (
                                <Tr>
                                  <Td colSpan={3}>
                                    <Center>No Accounts to Display</Center>
                                  </Td>
                                </Tr>
                              )}
                              {wo?.accounts?.map((a, i) => (
                                <Tr key={i}>
                                  <Td>
                                    {
                                      allSubAccounts?.find(
                                        (x) => x.subAccountId === a.subAccountId
                                      )?.fullSubAccountName
                                    }
                                  </Td>
                                  <Td>
                                    <CurrencyLabel value={a.pending} />
                                  </Td>
                                  <Td>
                                    <CurrencyLabel value={a.total} />
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </Stack>
                    </TabPanel>
                    {pendingRevision && (
                      <TabPanel>
                        <ApproversSection
                          entityName={wo?.name ?? ''}
                          revision={pendingRevision}
                        />
                      </TabPanel>
                    )}
                    <TabPanel>
                      <TableContainer>
                        <Table variant="simple">
                          <Thead>
                            <Tr>
                              <Th></Th>
                              <Th>Name</Th>
                              <Th>Total</Th>
                              <Th>Requested</Th>
                              <Th>Redeemed</Th>
                              <Th>Cancelled</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {transactions?.items.map((t, i) => (
                              <>
                                <Tr
                                  key={i}
                                  cursor="grab"
                                  onClick={() =>
                                    setToggledRevisionRow(
                                      (toggledRevisionRow?.id !== t.id &&
                                        toggledRevisionRow) ||
                                        undefined
                                    )
                                  }
                                >
                                  <Td>
                                    <ApprovalsArrayIcon txn={t} />
                                  </Td>
                                  <Td>{t.name}</Td>
                                  <Td>
                                    <CurrencyLabel value={t.total} />
                                  </Td>
                                  <Td>
                                    <DateDisplay value={t.requested} />
                                  </Td>
                                  <Td>
                                    <DateDisplay value={t.redeemed} />
                                  </Td>
                                  <Td>
                                    <DateDisplay value={t.cancelled} />
                                  </Td>
                                </Tr>
                                {toggledRevisionRow?.id === t.id && (
                                  <Tr>
                                    <Td colSpan={6}>
                                      <RevisionApprovalTable
                                        approvals={t.approvals}
                                        onDecisionRequested={(a) =>
                                          setDecisionDrawer({
                                            entityId: wo?.id ?? "0",
                                            entityName: wo?.name ?? '',
                                            revisionName: t.name,
                                            transactionId: t.transactionId,
                                            transactionType: t.transactionType,
                                            amount: t.total,
                                            ownerId: a.userId,
                                            isOpen: true,
                                          })
                                        }
                                      />
                                    </Td>
                                  </Tr>
                                )}
                              </>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </TabPanel>
                    <TabPanel>
                      <DateStack
                        dates={[
                          { label: 'Committed', value: wo?.commitmentDate },
                          // { label: 'Created', value: wo?.createdAt },
                          // { label: 'Last Updated', value: wo?.updatedAt },
                        ]}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </PageBody>
            </Flex>
          </Box>

          {/* </Card> */}
        </Stack>
      </LoadingSkeleton>
    </Box>
  );
};
